import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { Footer, MarketingHeader, SEO } from '@/components';
import { WeekGrid } from '@/components/parenting-plan';
import { track } from '@/lib/analytics';
import {
  SCHEDULE_PAGE_META,
  isScheduleSlug,
  type ParentingPlanScheduleSlug,
} from '@/lib/parenting-plan-schemas';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

export const Route = createFileRoute('/parenting-plan/$schedule')({
  beforeLoad: ({ params }) => {
    if (!isScheduleSlug(params.schedule)) {
      throw redirect({ to: '/parenting-plan' });
    }
  },
  component: ScheduleLandingPage,
});

function ScheduleLandingPage() {
  const { schedule } = Route.useParams();
  const meta = SCHEDULE_PAGE_META[schedule as ParentingPlanScheduleSlug];

  useEffect(() => {
    track('Parenting Plan Schedule Page View', { schedule });
  }, [schedule]);

  const path = `/parenting-plan/${schedule}`;

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title={`${meta.title}: Free builder | Harbor`}
        description={meta.description}
        path={path}
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-10'>
        <div className='mx-auto max-w-4xl px-6'>
          <h1
            className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'
            style={{ ...nunito, fontWeight: 700 }}
          >
            {meta.title}
          </h1>
          <p
            className='text-lg leading-relaxed text-gray-600 md:text-xl'
            style={{ ...nunito, fontWeight: 400 }}
          >
            {meta.description}
          </p>
          <div className='mt-8'>
            <Link
              to='/parenting-plan/builder'
              search={{ schedule, claimToken: undefined }}
              className='inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-4 text-lg font-semibold text-white shadow-lg transition-colors hover:bg-primary-hover'
              style={{ ...nunito, fontWeight: 600 }}
            >
              Build this plan in the wizard <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      <div className='mx-auto max-w-3xl px-6 py-12'>
        <h2 className='mb-4 text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
          Two-week illustration
        </h2>
        <WeekGrid pattern={meta.pattern14} />
        <div
          className='mt-10 rounded-lg border border-amber-200 bg-amber-50 p-6'
          role='note'
          style={nunito}
        >
          <p className='text-gray-800 leading-relaxed'>
            <strong className='font-semibold'>Disclaimer.</strong> Schedules vary by family, school
            district, and court. This page is educational only and does not constitute legal advice.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
